import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastService } from '@shared/services/toast/toast.service';
import { FileService } from '@shared/services/file/file.service';
import { FeedbackService } from '@core/services/feedback/feedback.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { SharedModule } from '@shared/shared.module';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FieldErrorComponent } from '@standalone/components/field-error/field-error.component';
import { wrapValidator } from '@shared/functions/wrap-validator';
import { DialogComponent } from '@standalone/components/dialog/dialog.component';
import { AuthService } from '@core/services/auth/auth.service';

@Component({
  selector: 'app-feedback-popup',
  templateUrl: './feedback-popup.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    SharedModule,
    TranslocoPipe,
    FieldErrorComponent,
    DialogComponent,
  ],
})
export class FeedbackPopupComponent {
  public form: FormGroup = new FormGroup({
    type: new FormControl(null, [Validators.required]),
    description: new FormControl('', [
      wrapValidator(
        Validators.required,
        this.transloco.translate('errors.fieldRequired')
      ),
      wrapValidator(
        Validators.maxLength(100),
        this.transloco.translate('errors.maxLength', { value: 100 })
      ),
    ]),
    contact: new FormControl('', [
      wrapValidator(
        Validators.required,
        this.transloco.translate('errors.fieldRequired')
      ),
      wrapValidator(
        Validators.maxLength(20),
        this.transloco.translate('errors.maxLength', { value: 20 })
      ),
    ]),
    files: new FormControl([]),
  });

  public types: string[] = [
    'bug',
    'review',
    'suggestion',
    'requestItem',
    'incorrectTranslation',
    'other',
  ];

  constructor(
    private dialogRef: MatDialogRef<FeedbackPopupComponent>,
    private toastService: ToastService,
    private fileService: FileService,
    private feedbackService: FeedbackService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public transloco: TranslocoService,
    public authService: AuthService
  ) {}

  public isAuthenticated(): boolean {
    return this.authService.isAuthenticated$.value;
  }

  public uploadFiles(files: FileList): void {
    if (this.form.get('files')?.value?.length + files.length > 5) {
      this.toastService.open(
        this.transloco.translate('validations.maxFileLimit', { limit: 5 }),
        'error'
      );
      return;
    }
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      if (files[i] && files[i].size > 2e6) {
        this.toastService.open(
          this.transloco.translate('validations.maxFileSize', {
            name: files[i].name,
          }),
          'error'
        );
        return;
      }
      formData.append('files', files[i]);
    }
    this.fileService.uploadFeedbackImages(formData).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.form
            .get('files')
            ?.patchValue([...this.form.get('files')?.value, ...response.data]);
        }
      },
      error: (err) => console.error('An error occurred :', err),
    });
  }

  public deleteFiles(id: string): void {
    const images = this.form
      .get('files')
      ?.value.filter((file: any) => file._id !== id);
    this.form.get('files')?.patchValue(images);
  }

  public onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    const data = {
      ...this.form.getRawValue(),
      files: this.form.getRawValue().files.map((e: any) => e._id),
    };

    this.feedbackService.sendFeedback(data).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.dialogRef.close();
          this.toastService.open(
            this.transloco.translate('messages.feedbackSent'),
            'success'
          );
        }
      },
      error: (err) => console.error('An error occurred :', err),
    });
  }
}
